import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { colors } from 'styles/colors';
import images from 'assets/images/images';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { clarityEvent } from 'utils/hooks/useClarity';

interface ConsultationBookingModalProps {
  open: boolean;
  onClose: () => void;
  onScheduleNow: () => void;
  onScheduleLater: () => void;
  userEmail?: string;
  userId?: string;
}

const ConsultationBookingModal: React.FC<ConsultationBookingModalProps> = ({
  open,
  onClose,
  onScheduleNow,
  onScheduleLater,
  userEmail,
  userId,
}) => {
  const handleScheduleNow = () => {
    if (userId) {
      clarityEvent("selfcare_proceed_to_clovemind", {
        userId,
        source: "booking_modal"
      });
    }
    onScheduleNow();
  };

  const handleScheduleLater = () => {
    if (userId) {
      clarityEvent("selfcare_schedule_later", {
        userId
      });
    }
    onScheduleLater();
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          // height: '648px',
          width: '530px',
          minHeight: '500px',
          position: 'relative',
          bgcolor: '#F5E6E8',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
        }
      }}
    >
      {/* Decorative orange circles */}
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -45,
          width: 120,
          height: 120,
          bgcolor: '#F68868',
          borderRadius: '50%',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -18,
          left: -50,
          width: 100,
          height: 100,
          bgcolor: '#F68868',
          borderRadius: '50%',
          zIndex: 0,
        }}
      />

      {/* Small decorative dots */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: 25,
          width: 12,
          height: 12,
          bgcolor: '#F68868',
          borderRadius: '50%',
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 35,
          left: 40,
          width: 8,
          height: 8,
          bgcolor: '#FBA48A',
          borderRadius: '50%',
          zIndex: 1,
        }}
      />

      {/* Small decorative dots - bottom right */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          right: 25,
          width: 12,
          height: 12,
          bgcolor: '#F68868',
          borderRadius: '50%',
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 35,
          right: 40,
          width: 8,
          height: 8,
          bgcolor: '#FBA48A',
          borderRadius: '50%',
          zIndex: 1,
        }}
      />

      <DialogContent sx={{ px: 4, pt: 8, pb: 5, textAlign: 'center', position: 'relative', zIndex: 1 }}>
        {/* Success message */}
        <Typography
          sx={{
            color: colors.fontGrey,
            fontSize: {xs: "18px", sm:'20px'},
            fontWeight: 500,
            mb: {xs: 2, sm: 3},
            lineHeight: 1.2,
          }}
        >
          Schedule a Session
        </Typography>

        {/* Important instructions alert */}
        <Box
          sx={{
            bgcolor: '#FFFBEB',
            borderRadius: '16px',
            border: '1px solid #FDE68A',
            px: {xs: 2, sm: 3},
            pt: {xs: 2, sm: 3},
            pb: 1,
            mb: {xs: 2, sm: 3},
            textAlign: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mb: 1.5 }}>
            <InfoOutlinedIcon sx={{ color: colors.fontBrown, fontSize: 18, mr: 1 }} />
            <Typography
              sx={{
                color: colors.fontBrown,
                fontSize: {xs: "16px", sm:'18px'},
                fontWeight: 600,
              }}
            >
              Important Instructions
            </Typography>
          </Box>

          <Box 
            component="ol" 
            sx={{ 
              textAlign: 'left', 
              paddingLeft: '20px', 
              color: colors.fontBrown,
              margin: 0
            }}
          >
            <Box 
              component="li" 
              sx={{ 
                fontSize: {xs: "14px", sm:'16px'}, 
                lineHeight: 1.4,
                mb: 1
              }}
            >
              Login with the same email ID used for ProSculpt. Your registered email ID is: <Box component="span" sx={{ fontWeight: 'bold' }}>{userEmail}</Box>
            </Box>
            <Box 
              component="li" 
              sx={{ 
                fontSize: {xs: "14px", sm:'16px'}, 
                lineHeight: 1.4,
                mb: 1
              }}
            >
              Enter the OTP received in your email.
            </Box>
            <Box 
              component="li" 
              sx={{ 
                fontSize: {xs: "14px", sm:'16px'}, 
                lineHeight: 1.4,
                mb: 1
              }}
            >
              Choose your preferred therapist.
            </Box>
            <Box 
              component="li" 
              sx={{ 
                fontSize: {xs: "14px", sm:'16px'}, 
                lineHeight: 1.4
              }}
            >
              Book your appointment on Clovemind at your convenience.
            </Box>
          </Box>
        </Box>


        {/* Footer text */}
        <Typography
          sx={{
            color: '#6B7280',
            fontSize: {xs: '12px',sm:'14px'},
            fontWeight: 400,
            mt: 2,
            pb:2,
            lineHeight: 1.4,
            // maxWidth: '90%',
            margin: '24px auto 0',
          }}
        >
          Note: Once a session is purchased, it can be scheduled only once.
        </Typography>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={handleScheduleNow}
            sx={{
              height: "34px",
              fontSize: "14px",
              fontWeight: 500,
              px: "24px",
              py: "8px",
              borderRadius: "4px",
              bgcolor: colors.primaryBlue,
              color: '#ffffff',
              textTransform: 'none',
              boxShadow: 'none',
              width: {sx: "230px", sm:'272px'},
              '&:hover': {
                bgcolor: colors.primaryBlueDark,
                boxShadow: 'none',
              },
            }}
          >
            Proceed to Clovemind
          </Button>

          <Button
            variant="outlined"
            onClick={handleScheduleLater}
            sx={{
              height: "34px",
              fontSize: "14px",
              fontWeight: 500,
              px: "24px",
              py: "8px",
              borderRadius: "4px",
              color: colors.primaryBlue,
              borderColor: colors.primaryBlue,
              textTransform: 'none',
              bgcolor: '#FAFAFB',
              width: {sx: "190px", sm:'225px'},
              '&:hover': {
                bgcolor: '#EBF4FF',
                borderColor: colors.primaryBlue,
              },
            }}
          >
            Schedule Later
          </Button>
        </Box>

        
      </DialogContent>
    </Dialog>
  );
};

export default ConsultationBookingModal;